/**
 * routes/rc/session/$sessionId.tsx — Live RC room.
 *
 * Lives at `/rc/session/$sessionId`. Fetches session metadata in the
 * loader, computes the WebSocket URL on the client, and mounts
 * <GameSessionRoom> with all the props the existing component needs.
 *
 * Phase 7 v2 Task 6.
 */
import { useMemo, type ReactNode } from 'react';
import { createFileRoute, redirect, notFound } from '@tanstack/react-router';
import { z } from 'zod';
import { GameSessionRoom } from '../../../components/rc/GameSessionRoom.js';
import { RcSessionLoadFailure } from '../../../components/rc/RcSessionLoadFailure.js';
import { freshSessionQuery } from '../../../lib/api/queries.js';
import { RcSessionLoadError, rcSessionQuery } from '../../../lib/api/queries-rc.js';
import { fetchApi } from '../../../lib/api/client.js';
import { resolveGameBundlePath } from '../../../lib/game-bundle-path.js';

/**
 * `?view=game` — game-only share-screen mode. Renders just the game stage
 * (no sidebar, header, banner, workspace or tutor console) so a tutor can
 * share this single tab while the controls stay open in the other tab.
 * Unknown values fall back to the full room instead of failing the route.
 */
const rcSessionSearchSchema = z.object({
  view: z.enum(['game']).optional().catch(undefined),
});

export const Route = createFileRoute('/rc/session/$sessionId')({
  validateSearch: (search) => rcSessionSearchSchema.parse(search),
  beforeLoad: async ({ context, params, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: 'session', redirect: location.href },
      });
    }
    return { user, sessionId: params.sessionId };
  },
  loader: async ({ context, params }) => {
    // Confirm the session exists before mounting the room. The DO will
    // also reject stale sessionIds on the WS handshake.
    const res = await context.queryClient.fetchQuery(rcSessionQuery(params.sessionId));
    if (!res.ok) throw notFound();
    return { sessionMeta: res.data };
  },
  component: RcSessionPage,
  // F-6: a parent (or any non-participant) used to land on the generic
  // "Something went wrong!" boundary. Show a plain-language screen instead.
  errorComponent: ({ error }) => (
    <RcSessionLoadFailure
      code={error instanceof RcSessionLoadError ? error.code : 'unknown'}
    />
  ),
});

function RcSessionPage(): ReactNode {
  const { user, sessionId } = Route.useRouteContext();
  const { sessionMeta } = Route.useLoaderData();
  const { view } = Route.useSearch();
  const wsUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${window.location.host}/api/rc/sessions/${encodeURIComponent(sessionId)}/ws`;
  }, [sessionId]);
  const iframeSrc = resolveGameBundlePath(sessionMeta.game_id);
  const isTutor =
    user.kind === 'tutor' || user.kind === 'admin' || user.role === 'admin';

  // identityToken is fetched inside <GameSessionRoom> on mount via
  // a useEffect that calls /api/rc/sessions/:id/identity. We pass
  // an empty placeholder here; the component will hydrate it before
  // opening the WebSocket.
  return (
    <GameSessionRoom
      sessionId={sessionId}
      identityToken=""
      currentUserId={user.userId}
      isTutor={isTutor}
      wsUrl={wsUrl}
      iframeSrc={iframeSrc}
      classroomId={sessionMeta.classroom_id ?? undefined}
      gameOnly={view === 'game'}
    />
  );
}
